import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";

import {
  createEvent,
  deleteEvent,
  getMalaysiaHolidays,
  getPreferences,
  listEvents,
  savePreferences,
  type EventRow,
  type MalaysiaHoliday,
  type MalaysiaHolidayResponse,
} from "../lib/webAppApi";

import {
  ExpandableDateDetails,
} from "../features/calendar/ExpandableDateDetails";

import {
  LocationPicker,
  type EventLocation,
} from "../features/calendar/LocationPicker";

import "../features/calendar/CalendarModal.css";

const DAYS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
];

const DEFAULT_REGIONS = [
  "Kuala Lumpur",
  "Labuan",
  "Putrajaya",
  "Johor",
  "Kedah",
  "Kelantan",
  "Melaka",
  "Negeri Sembilan",
  "Pahang",
  "Perak",
  "Perlis",
  "Pulau Pinang",
  "Sabah",
  "Sarawak",
  "Selangor",
  "Terengganu",
];

function errorMessage(error: unknown) {
  if (
    error instanceof Error &&
    error.message
  ) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}

function normaliseState(value: string) {
  const result =
    value.trim().toLowerCase();

  if (result === "penang") {
    return "pulau pinang";
  }

  if (
    result === "n. sembilan" ||
    result === "nsembilan"
  ) {
    return "negeri sembilan";
  }

  return result;
}

function holidayApplies(
  holiday: MalaysiaHoliday,
  region: string,
) {
  if (region === "All Malaysia") {
    return true;
  }

  const requested =
    normaliseState(region);

  return holiday.states.some(
    (state) =>
      state === "All Malaysia" ||
      normaliseState(state) === requested,
  );
}

function localDateKey(date: Date) {
  return [
    date.getFullYear(),
    String(
      date.getMonth() + 1,
    ).padStart(2, "0"),
    String(
      date.getDate(),
    ).padStart(2, "0"),
  ].join("-");
}

function eventDateKey(timestamp: number) {
  return localDateKey(
    new Date(timestamp),
  );
}

function sameDate(
  left: Date,
  right: Date,
) {
  return (
    left.getFullYear() ===
      right.getFullYear() &&
    left.getMonth() ===
      right.getMonth() &&
    left.getDate() ===
      right.getDate()
  );
}

function localDateTimeValue(
  timestamp: number,
) {
  const date = new Date(timestamp);

  return new Date(
    timestamp -
      date.getTimezoneOffset() *
        60_000,
  )
    .toISOString()
    .slice(0, 16);
}

function formatSelectedDate(
  date: Date,
) {
  return new Intl.DateTimeFormat(
    "en-MY",
    {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  ).format(date);
}

function createCalendarCells(
  year: number,
  month: number,
) {
  const first =
    new Date(year, month, 1);

  const startingDate =
    new Date(year, month, 1);

  startingDate.setDate(
    first.getDate() -
      first.getDay(),
  );

  return Array.from(
    { length: 42 },
    (_, index) => {
      const date =
        new Date(startingDate);

      date.setDate(
        startingDate.getDate() +
          index,
      );

      return date;
    },
  );
}

function EventDialog({
  selectedDate,
  close,
  saved,
}: {
  selectedDate: Date;
  close: () => void;
  saved: () => Promise<void>;
}) {
  const initialStart =
    new Date(selectedDate);

  initialStart.setHours(
    9,
    0,
    0,
    0,
  );

  const initialEnd =
    new Date(initialStart);

  initialEnd.setHours(
    initialStart.getHours() + 1,
  );

  const [title, setTitle] =
    useState("");

  const [category, setCategory] =
    useState("Personal");

  const [startAt, setStartAt] =
    useState(
      localDateTimeValue(
        initialStart.getTime(),
      ),
    );

  const [endAt, setEndAt] =
    useState(
      localDateTimeValue(
        initialEnd.getTime(),
      ),
    );

  const [
    eventLocation,
    setEventLocation,
  ] = useState<EventLocation>({
    location: "",
  });

  const [notes, setNotes] =
    useState("");

  const [allDay, setAllDay] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  async function submit(
    event: FormEvent,
  ) {
    event.preventDefault();

    setSaving(true);
    setError("");

    try {
      const start =
        new Date(startAt).getTime();

      const end =
        new Date(endAt).getTime();

      if (
        !Number.isFinite(start) ||
        !Number.isFinite(end)
      ) {
        throw new Error(
          "Choose a valid event date and time.",
        );
      }

      await createEvent({
        title,
        category,
        startAt: start,
        endAt: Math.max(start, end),
        allDay,

        location:
          eventLocation.location,

        latitude:
          eventLocation.latitude,

        longitude:
          eventLocation.longitude,

        placeId:
          eventLocation.placeId,

        notes,
      });

      await saved();
      close();
    } catch (caught) {
      setError(
        errorMessage(caught),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="calendar-modal-backdrop"
      role="presentation"
      onMouseDown={close}
    >
      <section
        className="calendar-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-event-title"
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >
        <header>
          <div>
            <span>PERSONAL EVENT</span>

            <h2 id="new-event-title">
              Add an event
            </h2>
          </div>

          <button
            type="button"
            className="calendar-close"
            onClick={close}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <form
          className="calendar-event-form"
          onSubmit={submit}
        >
          {error && (
            <div className="message error">
              {error}
            </div>
          )}

          <label>
            Event title

            <input
              value={title}
              required
              autoFocus
              maxLength={256}
              placeholder="e.g. Birthday dinner"
              onChange={(event) =>
                setTitle(
                  event.target.value,
                )
              }
            />
          </label>

          <label>
            Category

            <input
              value={category}
              maxLength={128}
              placeholder="Personal"
              onChange={(event) =>
                setCategory(
                  event.target.value,
                )
              }
            />
          </label>

          <div className="calendar-form-row">
            <label>
              Starts

              <input
                type="datetime-local"
                value={startAt}
                required
                disabled={allDay}
                onChange={(event) =>
                  setStartAt(
                    event.target.value,
                  )
                }
              />
            </label>

            <label>
              Ends

              <input
                type="datetime-local"
                value={endAt}
                required
                disabled={allDay}
                onChange={(event) =>
                  setEndAt(
                    event.target.value,
                  )
                }
              />
            </label>
          </div>

          <LocationPicker
            value={eventLocation}
            onChange={setEventLocation}
          />

          <label>
            Notes

            <textarea
              value={notes}
              rows={3}
              maxLength={4000}
              placeholder="Anything guests or you should know"
              onChange={(event) =>
                setNotes(
                  event.target.value,
                )
              }
            />
          </label>

          <label className="calendar-all-day">
            <span>
              <strong>
                All-day event
              </strong>

              <small>
                Do not display a specific time
              </small>
            </span>

            <input
              type="checkbox"
              checked={allDay}
              onChange={(event) =>
                setAllDay(
                  event.target.checked,
                )
              }
            />
          </label>

          <footer>
            <button
              className="button secondary"
              type="button"
              onClick={close}
            >
              Cancel
            </button>

            <button
              className="button primary"
              type="submit"
              disabled={saving}
            >
              {saving
                ? "Saving…"
                : "Create event"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

export function CalendarPage() {
  const today = useMemo(
    () => new Date(),
    [],
  );

  const [viewDate, setViewDate] =
    useState(
      new Date(
        today.getFullYear(),
        today.getMonth(),
        1,
      ),
    );

  const [
    selectedDate,
    setSelectedDate,
  ] = useState(today);

  const [events, setEvents] =
    useState<EventRow[]>([]);

  const [
    officialCalendar,
    setOfficialCalendar,
  ] = useState<
    MalaysiaHolidayResponse | null
  >(null);

  const [region, setRegion] =
    useState("All Malaysia");

  const [loadingEvents, setLoadingEvents] =
    useState(true);

  const [
    loadingHolidays,
    setLoadingHolidays,
  ] = useState(true);

  const [eventsError, setEventsError] =
    useState("");

  const [
    holidaysError,
    setHolidaysError,
  ] = useState("");

  const [showForm, setShowForm] =
    useState(false);

  const year =
    viewDate.getFullYear();

  const month =
    viewDate.getMonth();

  const cells = useMemo(
    () =>
      createCalendarCells(
        year,
        month,
      ),
    [year, month],
  );

  const regions = useMemo(
    () => [
      "All Malaysia",
      ...(
        officialCalendar?.regions ??
        DEFAULT_REGIONS
      ),
    ].filter(
      (value, index, array) =>
        array.indexOf(value) ===
        index,
    ),
    [officialCalendar],
  );

  const visibleHolidays = useMemo(
    () =>
      (
        officialCalendar?.holidays ?? []
      ).filter((holiday) =>
        holidayApplies(
          holiday,
          region,
        ),
      ),
    [
      officialCalendar,
      region,
    ],
  );

  const selectedKey =
    localDateKey(selectedDate);

  const selectedHolidays =
    visibleHolidays.filter(
      (holiday) =>
        holiday.date === selectedKey,
    );

  const selectedEvents =
    events.filter(
      (event) =>
        eventDateKey(event.startAt) ===
        selectedKey,
    );

  async function loadEvents() {
    setLoadingEvents(true);
    setEventsError("");

    try {
      setEvents(await listEvents());
    } catch (caught) {
      setEventsError(
        errorMessage(caught),
      );
    } finally {
      setLoadingEvents(false);
    }
  }

  async function loadHolidays(
    requestedYear: number,
  ) {
    setLoadingHolidays(true);
    setHolidaysError("");

    try {
      setOfficialCalendar(
        await getMalaysiaHolidays(
          requestedYear,
        ),
      );
    } catch (caught) {
      setOfficialCalendar(null);

      setHolidaysError(
        errorMessage(caught),
      );
    } finally {
      setLoadingHolidays(false);
    }
  }

  useEffect(() => {
    void loadEvents();

    void getPreferences()
      .then((preferences) => {
        const savedState =
          preferences?.myState;

        if (savedState) {
          setRegion(
            savedState === "Penang"
              ? "Pulau Pinang"
              : savedState,
          );
        }
      })
      .catch(() => {
        // Calendar works without preferences.
      });
  }, []);

  useEffect(() => {
    void loadHolidays(year);
  }, [year]);

  function moveMonth(amount: number) {
    const next = new Date(
      year,
      month + amount,
      1,
    );

    setViewDate(next);
    setSelectedDate(next);
  }

  function goToday() {
    const current = new Date();

    setViewDate(
      new Date(
        current.getFullYear(),
        current.getMonth(),
        1,
      ),
    );

    setSelectedDate(current);
  }

  function chooseDate(date: Date) {
    setSelectedDate(date);

    if (
      date.getMonth() !== month ||
      date.getFullYear() !== year
    ) {
      setViewDate(
        new Date(
          date.getFullYear(),
          date.getMonth(),
          1,
        ),
      );
    }
  }

  async function changeRegion(
    value: string,
  ) {
    setRegion(value);

    try {
      await savePreferences({
        myState:
          value === "Pulau Pinang"
            ? "Penang"
            : value,
      });
    } catch {
      // The local filter still works.
    }
  }

  return (
    <main className="webapp-page">
      <header className="calendar-page-header">
        <div>
          <span>MALAYSIA CALENDAR</span>

          <h1>Calendar</h1>

          <p>
            Official Malaysian public
            holidays and your private
            Appwrite events together.
          </p>
        </div>

        <button
          className="button primary"
          type="button"
          onClick={() => setShowForm(true)}
        >
          Add event
        </button>
      </header>

      <section className="calendar-controls glass-card">
        <label>
          Country

          <select disabled value="Malaysia">
            <option>Malaysia</option>
          </select>
        </label>

        <label>
          State or region

          <select
            value={region}
            onChange={(event) =>
              void changeRegion(
                event.target.value,
              )
            }
          >
            {regions.map((state) => (
              <option
                key={state}
                value={state}
              >
                {state}
              </option>
            ))}
          </select>
        </label>

        <label>
          Year

          <select
            value={year}
            onChange={(event) => {
              const selectedYear =
                Number(event.target.value);

              const next = new Date(
                selectedYear,
                month,
                1,
              );

              setViewDate(next);
              setSelectedDate(next);
            }}
          >
            {Array.from(
              { length: 5 },
              (_, index) =>
                today.getFullYear() -
                2 +
                index,
            ).map((availableYear) => (
              <option
                key={availableYear}
                value={availableYear}
              >
                {availableYear}
              </option>
            ))}
          </select>
        </label>
      </section>

      {holidaysError && (
        <section className="calendar-source-error">
          <div>
            <strong>
              Official calendar temporarily unavailable
            </strong>

            <p>{holidaysError}</p>
          </div>

          <button
            className="button secondary"
            type="button"
            onClick={() =>
              void loadHolidays(year)
            }
          >
            Retry
          </button>
        </section>
      )}

      {eventsError && (
        <section className="message error">
          <strong>
            Personal events could not be loaded.
          </strong>

          <p>{eventsError}</p>

          <button
            className="button secondary"
            type="button"
            onClick={() =>
              void loadEvents()
            }
          >
            Retry
          </button>
        </section>
      )}

      <section className="official-calendar glass-card">
        <header className="official-calendar-toolbar">
          <div className="calendar-month-buttons">
            <button
              type="button"
              onClick={() => moveMonth(-1)}
              aria-label="Previous month"
            >
              ‹
            </button>

            <button
              type="button"
              onClick={goToday}
            >
              Today
            </button>
          </div>

          <div className="calendar-month-title">
            <span>
              {loadingHolidays
                ? "Loading official dates…"
                : region}
            </span>

            <h2>
              {new Intl.DateTimeFormat(
                "en-MY",
                {
                  month: "long",
                  year: "numeric",
                },
              ).format(viewDate)}
            </h2>
          </div>

          <button
            type="button"
            onClick={() => moveMonth(1)}
            aria-label="Next month"
          >
            ›
          </button>
        </header>

        <div className="official-calendar-weekdays">
          {DAYS.map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>

        <div
          className="official-calendar-grid"
          aria-label="Monthly calendar"
        >
          {cells.map((date) => {
            const dateKey =
              localDateKey(date);

            const dayHolidays =
              visibleHolidays.filter(
                (holiday) =>
                  holiday.date === dateKey,
              );

            const dayEvents =
              events.filter(
                (event) =>
                  eventDateKey(
                    event.startAt,
                  ) === dateKey,
              );

            const isToday =
              sameDate(date, today);

            const isSelected =
              sameDate(
                date,
                selectedDate,
              );

            const outsideMonth =
              date.getMonth() !== month;

            const hasHoliday =
              dayHolidays.length > 0;

            const classes = [
              "official-calendar-cell",
              outsideMonth
                ? "outside-month"
                : "",
              isToday
                ? "is-today"
                : "",
              isSelected
                ? "is-selected"
                : "",
              hasHoliday
                ? "has-public-holiday"
                : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                type="button"
                className={classes}
                key={dateKey}
                onClick={() =>
                  chooseDate(date)
                }
                aria-label={formatSelectedDate(
                  date,
                )}
              >
                <span className="calendar-date-number">
                  {date.getDate()}
                </span>

                <div className="calendar-cell-items">
                  {dayHolidays
                    .slice(0, 2)
                    .map((holiday) => (
                      <span
                        className="calendar-holiday-label"
                        key={holiday.id}
                        title={holiday.name}
                      >
                        PH · {holiday.name}
                      </span>
                    ))}

                  {dayEvents
                    .slice(
                      0,
                      hasHoliday ? 1 : 2,
                    )
                    .map((event) => (
                      <span
                        className="calendar-personal-label"
                        key={event.$id}
                        title={event.title}
                      >
                        {event.title}
                      </span>
                    ))}

                  {dayHolidays.length +
                    dayEvents.length >
                    2 && (
                    <small>
                      +
                      {dayHolidays.length +
                        dayEvents.length -
                        2}{" "}
                      more
                    </small>
                  )}
                </div>

                <span className="calendar-mobile-markers">
                  {hasHoliday && (
                    <i className="holiday-marker" />
                  )}

                  {dayEvents.length > 0 && (
                    <i className="event-marker" />
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <footer className="calendar-legend">
          <span>
            <i className="legend-today" />
            Today
          </span>

          <span>
            <i className="legend-selected" />
            Selected
          </span>

          <span>
            <i className="legend-holiday" />
            Public holiday
          </span>

          <span>
            <i className="legend-event" />
            Personal event
          </span>
        </footer>
      </section>

      <ExpandableDateDetails
        selectedDate={selectedDate}
        holidays={selectedHolidays}
        events={selectedEvents}
        loading={
          loadingHolidays ||
          loadingEvents
        }
        onAddEvent={() => setShowForm(true)}
        onDeleteEvent={async (event) => {
          if (
            !window.confirm(
              `Delete “${event.title}”?`,
            )
          ) {
            return;
          }

          try {
            await deleteEvent(event.$id);
            await loadEvents();
          } catch (caught) {
            setEventsError(
              errorMessage(caught),
            );
          }
        }}
      />

      {officialCalendar && (
        <section className="calendar-source">
          <p>
            Source:{" "}

            <a
              href={
                officialCalendar.sourceUrl
              }
              target="_blank"
              rel="noreferrer"
            >
              {officialCalendar.source}
            </a>
          </p>

          <p>
            Retrieved{" "}

            {new Intl.DateTimeFormat(
              "en-MY",
              {
                dateStyle: "medium",
                timeStyle: "short",
              },
            ).format(
              new Date(
                officialCalendar.fetchedAt,
              ),
            )}

            {officialCalendar.cached
              ? " · Temporary cache"
              : " · Live official document"}
          </p>
        </section>
      )}

      {showForm && (
        <EventDialog
          selectedDate={selectedDate}
          close={() => setShowForm(false)}
          saved={loadEvents}
        />
      )}
    </main>
  );
}